import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { assetUrl, formatPrice } from '@/lib/digitalApi';
import { resourceHref, formatMonthYear } from '@/constants/resourceSections';

export const ProductCover = ({ product, className = '' }) => (
  <div className={`relative overflow-hidden rounded-2xl bg-sand/60 ${className}`}>
    {product.cover_url ? (
      <img
        src={assetUrl(product.cover_url)}
        alt={product.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        loading="lazy"
      />
    ) : (
      <div className="w-full h-full grid place-items-center">
        <span className="font-serif-display italic text-5xl text-taupe/70">{(product.format || product.title || 'O').charAt(0)}</span>
      </div>
    )}
  </div>
);

const Meta = ({ product }) => {
  const isArticle = product.content_type === 'article';
  const parts = isArticle
    ? [product.kicker || 'Article', product.category_name]
    : [product.format || 'Digital', product.category_name];
  return (
    <div className="flex items-center gap-3 text-[10px] font-label tracking-widest uppercase text-taupe">
      {parts.filter(Boolean).map((t, i) => (
        <span key={t} className="flex items-center gap-3">
          {i > 0 && <span className="w-1 h-1 rounded-full bg-gold" aria-hidden />}
          {t}
        </span>
      ))}
    </div>
  );
};

export const ProductCard = ({ product, section }) => {
  const isArticle = product.content_type === 'article';
  return (
    <Link to={resourceHref(product, section)} className="group block min-w-0" data-testid={`digital-product-card-${product.slug}`}>
      <ProductCover product={product} className="aspect-[4/5]" />
      <div className="pt-6">
        <Meta product={product} />
        <div className="mt-3 flex items-start justify-between gap-4">
          <h3 className="font-serif-display text-2xl md:text-3xl tracking-tighter leading-tight text-matte">{product.title}</h3>
          <ArrowUpRight size={18} strokeWidth={1.2} className="mt-2 shrink-0 text-matte/40 group-hover:text-gold transition-colors duration-500" />
        </div>
        {product.short_description && (
          <p className="mt-3 text-matte/65 leading-relaxed text-[15px] line-clamp-3">{product.short_description}</p>
        )}
        <div className="mt-4 font-label text-xs tracking-widest uppercase text-matte" data-testid={`digital-product-price-${product.slug}`}>
          {isArticle
            ? [formatMonthYear(product.published_date), product.read_time].filter(Boolean).join(' \u00b7 ')
            : formatPrice(product.price_cents, product.is_free)}
        </div>
      </div>
    </Link>
  );
};
